import React, { memo, useMemo } from 'react'
import CommonFormFieldWrapper, { getFormInputErrors } from '../../../common/components/form-field-wrapper/CommonFormFieldWrapper'
import styles from './SignUpProviderConfirmation.module.scss'
import CommonPhoneInput from '../../../common/components/phone-input/CommonPhoneInput'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import FormWrapper from '../../../common/components/form-wrapper/FormWrapper'
import { useNavigate, useParams } from 'react-router-dom'
import Header from '../../../layouts/public-layout-form/components/Header'
import UserService from '../../../httpService/UserService'

type Props = {
}

type BusinessInformation = {
    code: string
    firstName: string
    lastName: string
    businessName: string
    password: string
    confirmPassword: string
    phoneNumber: string
}

const SignUpProviderConfirmation: React.FC<Props> = memo((props: Props) => {
    const { code } = useParams();
    const userService = useMemo(() => new UserService(), []);

    const navigate = useNavigate();
    

    const {
        register,
        getValues,
        watch,
        setValue,
        formState: { errors, isValid },
    } = useForm<BusinessInformation>({
        defaultValues: {
            code: code,
            firstName: '',
            lastName: '',
            businessName: '',
            phoneNumber: '',
        },
        reValidateMode: 'onSubmit',
        mode: 'onTouched'
    })

    const submit = async () => {
        if (!isValid) return;

        try {
            await userService.post('register/provider/confirm', getValues());
            navigate('/login');
        }
        catch (error: any) {
            toast.error(error.response.data.title);
        }
    }

    const validatePassword = (value: string) => {

        // Check if password is at least 10 characters long
        if (value.length === 0) {
          return "Password is required";
        }
    
        // Check if password is at least 10 characters long
        if (value.length < 10) {
          return "Password must be at least 10 characters long";
        }
    
        // Check if password contains at least one uppercase letter
        if (!/[A-Z]/.test(value)) {
          return "Password must contain at least one uppercase letter";
        }
    
        // Check if password contains at least one lowercase letter
        if (!/[a-z]/.test(value)) {
          return "Password must contain at least one lowercase letter";
        }
    
        // Check if password contains at least one number
        if (!/\d/.test(value)) {
          return "Password must contain at least one number";
        }
    
        // Check if password contains at least one special character
        if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(value)) {
          return "Password must contain at least one special character";
        }

        return true;
    }

    return (
        <div className={styles.container}>
            <Header />
            <div className={styles.contentContainer}>
                <div className={styles.pageContainer}>
                    <FormWrapper titleSection={{ title: 'Basic Information', subtitle: 'Tell us about your business.', buttonTitle: 'Proceed'}} onSaveAndContinue={submit} isNotValid={!isValid} >
                        <div className={styles.container}>
                            <div className={styles.formContainer}>
                                <div className={`formFieldInlineContainer ${styles.formInlineRow}`}>
                                    <CommonFormFieldWrapper title="First name" required={true} errors={getFormInputErrors(errors.firstName?.message)}>
                                        <input {...register('firstName', { required: 'First name is required' })} placeholder="Enter First Name" />
                                    </CommonFormFieldWrapper>
                                    <CommonFormFieldWrapper title="Last name" required={true} errors={getFormInputErrors(errors.lastName?.message)}>
                                        <input {...register('lastName', { required: 'Last name is required' })} placeholder="Enter Last Name" />
                                    </CommonFormFieldWrapper>
                                </div>
                                <span className='spacer' />
                                <CommonFormFieldWrapper title="Business Name" required={true} errors={getFormInputErrors(errors.businessName?.message)}>
                                    <input {...register('businessName', { required: 'Business Name is required' })} placeholder="Enter Business Name" />
                                </CommonFormFieldWrapper>
                                <span className='spacer' />
                                <CommonFormFieldWrapper title="Password" required={true} errors={getFormInputErrors(errors.password?.message)}>
                                    <input type='password' {...register('password', { validate: validatePassword})} placeholder="Enter Password" />
                                </CommonFormFieldWrapper>
                                <span className='spacer' />
                                <CommonFormFieldWrapper title="Confirm Password" required={true} errors={getFormInputErrors(errors.confirmPassword?.message)}>
                                    <input type='password' {...register('confirmPassword', { required: 'Password must be confirmed' , validate: value => value === watch('password') || "Passwords do not match" })} placeholder="Confirm Password" />
                                </CommonFormFieldWrapper>
                                <span className='spacer' />
                                <CommonFormFieldWrapper title="Phone number" required={true} errors={getFormInputErrors(errors.phoneNumber?.message)}>
                                    <CommonPhoneInput
                                    registerInputProps={register('phoneNumber', {required: 'Phone Number is required' , pattern: {
                                        value: /^\+(?:[0-9] ?){6,}[0-9]$/,
                                        message: 'Invalid phone number'
                                    }})} 
                                    value={watch('phoneNumber') ?? ''} 
                                    defaultCountry='ng' 
                                    onChange={(value) => setValue('phoneNumber', value, {
                                        shouldDirty: true
                                    })} />
                                </CommonFormFieldWrapper>
                            </div>
                        </div>
                    </FormWrapper>
                </div>
            </div>
        </div>
    )
})

export default SignUpProviderConfirmation
